import { useCallback, useEffect } from 'react';
import { graduate } from '@features/feature-rollout-toggle/store/slice';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { incrementV2NoteCount, resetV2NoteCount } from '../store/encounterToggleSlice';
import { selectEncounterToggleState } from '../store/selectors';

const FEATURE_KEY = 'feature_encounter_view_v2';
const GRADUATION_THRESHOLD = 3;

/**
 * Hook for managing encounter-specific toggle graduation logic
 *
 * Tracks consecutive V2 note generations and auto-graduates users
 * after 3 successful notes. Resets count when user switches to classic.
 *
 * Note: This hook should be used alongside useFeatureToggle in the parent component.
 */
export function useEncounterToggleGraduation() {
  const dispatch = useAppDispatch();
  const { toggle } = useEventTracking();

  const consecutiveV2Notes = useAppSelector(selectEncounterToggleState);

  // Record a V2 note generation
  const recordV2NoteGenerated = useCallback(() => {
    dispatch(incrementV2NoteCount());
  }, [dispatch]);

  // Auto-graduate when threshold is reached
  useEffect(() => {
    if (consecutiveV2Notes >= GRADUATION_THRESHOLD) {
      dispatch(graduate({ featureKey: FEATURE_KEY }));
      toggle.graduated({
        feature_key: FEATURE_KEY,
        notes_before_graduation: consecutiveV2Notes,
      });
    }
  }, [consecutiveV2Notes, dispatch, toggle]);

  // Reset count when user switches to classic
  const handleSwitchToClassic = useCallback(() => {
    dispatch(resetV2NoteCount());
  }, [dispatch]);

  return {
    recordV2NoteGenerated,
    consecutiveV2Notes,
    handleSwitchToClassic,
  };
}
